import React, { createRef } from "react";
import { Link, withRouter } from "react-router-dom";
import { Button, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faDownload,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";

import Brand from "services/BrandService";
import Toast from "services/Toast";
import Emitter from "services/Emitter";
import MyTable from "components/MyTable";
import Countries from "components/Countries";

/**
 * @component
 * @category Scenes
 * @subcategory Brand
 */
class Merchants extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            enforcement: {
                map: false,
                unauthorized: false,
            },
            countries: [],
        };
        this.onEnter = this.onEnter.bind(this);
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);
        this.tableRef = createRef();
    }

    componentDidMount() {
        Emitter.once("BRAND_MERCHANTS", this.onEnter);
    }

    componentWillUnmount() {
        Emitter.off("BRAND_MERCHANTS");
    }

    downloadSpreadsheet() {
        this.tableRef.current.CSV({
            filename: "brand-" + this.props.brandId + "-merchants.csv",
        });
    }

    onEnter() {
        Brand.merchants(this.props.brandId).then((data) => {
            if (data === false) {
                Toast.error(
                    "Something went wrong while loading the list of merchants"
                );
                return;
            }
            let list = [];
            const countries = {};
            data.merchants.forEach((row) => {
                row.type =
                    row.type_id === 1
                        ? "Online Retailer"
                        : "Marketplace Seller";
                list.push(row);
                if (
                    row.country_id &&
                    typeof countries[row.country_id] === "undefined"
                ) {
                    const country = Countries.GetByID(row.country_id);
                    countries[row.country_id] = {
                        value: row.country_id,
                        label: country.label,
                    };
                }
                row.gs = row.gs_rate !== 0;
            });
            data.enforcement.map = data.enforcement.map ? true : false;
            data.enforcement.unauthorized = data.enforcement.unauthorized
                ? true
                : false;
            this.setState(
                {
                    yesterday: data.yesterday,
                    enforcement: data.enforcement,
                    countries: Object.values(countries),
                },
                () => {
                    this.tableRef.current?.resetSettings();
                    this.tableRef.current?.populate(list);
                }
            );
        });
    }

    render() {
        const headers = [
                {
                    field: "name",
                    label: "Merchant",
                    width: "200px",
                    sortable: true,
                    searchable: true,
                },
                {
                    field: "website_name",
                    label: "Website",
                    width: "225px",
                    sortable: true,
                    searchable: true,
                },
                {
                    field: "country_id",
                    label: "Selling Country",
                    allowEmpty: true,
                    sortable: true,
                    options: this.state.countries,
                    width: "130px",
                },
                {
                    field: "automated",
                    label: "Automated",
                    width: "90px",
                    sortable: true,
                    allowEmpty: true,
                    options: ["Yes", "No"],
                },
                {
                    field: "type",
                    label: "Type",
                    sortable: true,
                    allowEmpty: true,
                    width: "125px",
                    options: ["Marketplace Seller", "Online Retailer"],
                },
                {
                    field: "products",
                    label: "Products",
                    width: "90px",
                    sortable: true,
                    allowEmpty: true,
                    options: ["< 50", "50 - 100", "100 - 250", "> 250"],
                },
                {
                    field: "frequency",
                    label: "Frequency",
                    width: "90px",
                    sortable: true,
                    allowEmpty: true,
                    options: [
                        { label: "1x/day", value: "daily" },
                        { label: "2x/day", value: "twice" },
                        { label: "3x/day", value: "thrice" },
                    ],
                },
                {
                    field: "gs",
                    label: "GS URLs",
                    width: "80px",
                    sortable: true,
                    allowEmpty: true,
                    hint: "Are any the tracked URLs from Google Shopping?",
                    options: ["Yes", "No"],
                },
                {
                    field: "gs_rate",
                    label: "% URLs from GS",
                    width: "125px",
                    sortable: true,
                    allowEmpty: true,
                    hint: "What % of URL's for this site and brand/dashboard came from GS versus another source",
                    options: [
                        { label: "0%", value: "= 0" },
                        { label: "< 25%", value: "< 25" },
                        { label: "25-50%", value: "25 - 50" },
                        { label: "50-75%", value: "50 - 75" },
                        { label: "> 75%", value: "> 75" },
                        { label: "100%", value: "= 100" },
                    ],
                },
            ],
            boolValue = (row, field) => (row[field] ? "yes" : "no"),
            renderBool = (row, field) => {
                return row[field] ? (
                    <FontAwesomeIcon
                        style={{
                            color: "green",
                        }}
                        icon={faCheck}
                    />
                ) : (
                    <FontAwesomeIcon style={{ color: "red" }} icon={faTimes} />
                );
            };

        if (this.state.enforcement.unauthorized) {
            headers.push({
                field: "authorized",
                label: "Authorized Seller",
                width: "130px",
                sortable: true,
                allowEmpty: true,
                options: ["Yes", "No"],
            });
        }
        if (this.state.enforcement.map) {
            headers.push({
                field: "violations",
                label: "MAP Violations",
                width: "130px",
                sortable: true,
            });
        }

        return (
            <Container className="pt-4">
                <span
                    style={{
                        position: "absolute",
                        top: "-40px",
                        right: "15px",
                    }}
                >
                    <Button
                        disabled={this.state.loading}
                        onClick={this.downloadSpreadsheet}
                        className="tdButton"
                        size="sm"
                        variant="success"
                    >
                        <FontAwesomeIcon icon={faDownload} />
                    </Button>
                </span>
                <MyTable
                    name="brand-merchants"
                    dynamic={false}
                    ref={this.tableRef}
                    sortBy="products"
                    sortOrder="desc"
                    headers={headers}
                    renderColumns={{
                        name: (row) => (
                            <Link className="link" to={`/merchant/${row.id}`}>
                                (#{row.id}) {row.name}
                            </Link>
                        ),
                        website_name: (row) => (
                            <Link
                                className="link"
                                to={`/website/${row.website_id}`}
                            >
                                (#{row.website_id}) {row.website_name}
                            </Link>
                        ),
                        country_id: (row) =>
                            Countries.GetByID(row.country_id)?.label ??
                            "Unknown",
                        type_id: (row) => row.type,
                        frequency: {
                            className: "text-center",
                        },
                        gs: {
                            format: renderBool,
                            className: "text-center",
                            text: boolValue,
                            csv: boolValue,
                        },
                        gs_rate: {
                            format: (row) => row.gs_rate.toFixed(2) + "%",
                            className: "text-center",
                        },
                        authorized: {
                            format: renderBool,
                            className: "text-center",
                            text: boolValue,
                            csv: boolValue,
                        },
                        automated: {
                            format: renderBool,
                            className: "text-center",
                            text: boolValue,
                            csv: boolValue,
                        },
                    }}
                />
            </Container>
        );
    }
}

export default withRouter(Merchants);
